import { z } from "zod";
import type { DomainCode } from "@/constants/domains";
import type { ActivityDefinition, ActivityLibrary, ActivityModule, ActivityStep } from "@/types/activity";
import type { ScaleEntry } from "@/types/scale";

const goalSchema = z
  .object({
    code: z.string().min(1),
    title: z.string().min(1),
    standard: z.string().trim(),
    examples: z.string().trim().default(""),
    activities: z.array(z.string().trim())
  })
  .strip();

const assessmentSchema = z
  .object({
    code: z.string().min(1),
    domain: z.object({
      code: z.string().min(1),
      name: z.string().min(1)
    }),
    subdomain: z.object({
      code: z.string().min(1),
      name: z.string().min(1)
    }),
    long_term_goal: goalSchema,
    short_term_goal: goalSchema.optional()
  })
  .strip();

const stepSchema = z
  .object({
    title: z.string().min(1),
    description: z.string().trim().default(""),
    assessments: z.array(assessmentSchema).default([])
  })
  .strip();

const moduleSchema = z
  .object({
    title: z.string().min(1),
    description: z.string().trim().optional(),
    materials: z.array(z.string().trim()).default([]),
    steps: z.array(stepSchema).optional(),
    assessments: z.array(assessmentSchema).optional()
  })
  .strip();

const activitySchema = z
  .object({
    code: z.string().min(1),
    name: z.string().min(1),
    modules: z.array(moduleSchema)
  })
  .strip();

const activityLibrarySchema = z
  .object({
    activities: z.array(activitySchema)
  })
  .strip();

type RawActivityStep = z.infer<typeof stepSchema>;
type RawAssessment = z.infer<typeof assessmentSchema>;
type RawActivityModule = z.infer<typeof moduleSchema>;

interface ParseOptions {
  validateAgainst?: Map<string, ScaleEntry>;
}

export function parseActivityLibrary(raw: unknown, options?: ParseOptions): ActivityLibrary {
  const data = activityLibrarySchema.parse(raw);

  const activityIndex = new Map<string, ActivityDefinition>();
  const activities = data.activities.map<ActivityDefinition>((activity) => {
    const modules = activity.modules.map<ActivityModule>((module, moduleIndex) => {
      const steps = resolveSteps(activity.code, module, moduleIndex);
      const normalizedSteps = steps.map<ActivityStep>((step) => {
        if (options?.validateAgainst) {
          for (const code of step.entryCodes) {
            if (!options.validateAgainst.has(code)) {
              throw new Error(`Activity step references unknown scale entry: ${code}`);
            }
          }
        }

        return {
          ...step,
          domainCodeSet: Array.from(new Set(step.domainCodeSet))
        };
      });

      return {
        title: module.title,
        description: module.description ?? "",
        materials: module.materials,
        steps: normalizedSteps
      };
    });

    const summary = modules.find((module) => module.description)?.description ?? "";

    const definition: ActivityDefinition = {
      code: activity.code,
      name: activity.name,
      summary,
      modules
    };

    activityIndex.set(definition.code, definition);
    return definition;
  });

  return { activities, activityIndex };
}

function resolveSteps(
  activityCode: string,
  module: RawActivityModule,
  moduleIndex: number,
): Array<ActivityStep & { domainCodeSet: DomainCode[] }> {
  const steps: RawActivityStep[] = module.steps ?? [];
  const hasStandaloneAssessments = module.assessments && module.assessments.length > 0;

  const normalized: Array<ActivityStep & { domainCodeSet: DomainCode[] }> = [];

  if (hasStandaloneAssessments) {
    normalized.push(
      buildStepFromAssessments(
        activityCode,
        moduleIndex,
        normalized.length,
        module.title,
        module.description ?? "",
        module.assessments ?? [],
      ),
    );
  }

  if (steps.length > 0) {
    steps.forEach((step) => {
      normalized.push(
        buildStepFromAssessments(
          activityCode,
          moduleIndex,
          normalized.length,
          step.title,
          step.description ?? "",
          step.assessments,
        ),
      );
    });
  }

  return normalized;
}

function buildStepFromAssessments(
  activityCode: string,
  moduleIndex: number,
  sequence: number,
  title: string,
  description: string,
  assessments: RawAssessment[],
): ActivityStep & { domainCodeSet: DomainCode[] } {
  const entryCodes = Array.from(
    new Set(assessments.map((assessment) => assessment.code.trim())),
  );
  const domainCodeSet = assessments.map((assessment) => assessment.domain.code.trim() as DomainCode);

  return {
    id: `${activityCode}-${moduleIndex}-${sequence}`,
    title,
    description,
    entryCodes,
    domainCodeSet
  };
}
